import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Home, Building2, Droplets, Wrench } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 },
};

// Real project images
const images = {
  blueTileAfter: "https://customer-assets.emergentagent.com/job_media-blasting-dark/artifacts/iqdzyqzo_After%202.jpeg",
  stoneAfter: "https://customer-assets.emergentagent.com/job_media-blasting-dark/artifacts/926zc2jx_After%201.jpeg",
  poolPrepAfter: "https://customer-assets.emergentagent.com/job_media-blasting-dark/artifacts/lncc0qq7_After%203.jpeg",
  graffitiRemoval: "https://customer-assets.emergentagent.com/job_media-blasting-dark/artifacts/6vcnur0y_04395FCB-6B4F-4A6A-824F-AFF6A83BDD46.jpeg",
  trailer: "https://customer-assets.emergentagent.com/job_media-blasting-dark/artifacts/9dz7dc7a_630F1579-8C3D-4D9D-8B24-7BA7D8C780AB.jpeg",
};

const industries = [
  {
    icon: Home,
    title: "Residential",
    description: "Pool owners, homeowners with stone surfaces, patio and driveway restoration. The core of our business.",
    services: ["Pool tile calcium removal", "Stone patio restoration", "Driveway cleaning", "Exterior wall cleaning"],
  },
  {
    icon: Building2,
    title: "Commercial Property",
    description: "Property managers, building owners, and commercial facilities requiring large-scale surface restoration.",
    services: ["Parking structures", "Building facades", "Common area restoration", "Graffiti removal"],
  },
  {
    icon: Droplets,
    title: "Pool Service Contractors",
    description: "Pool builders and service companies who need reliable surface prep and calcium removal for their clients.",
    services: ["Replaster preparation", "Calcium removal", "Tile restoration", "Surface profiling"],
  },
  {
    icon: Wrench,
    title: "Specialty & Custom",
    description: "Unique projects requiring precision blasting: custom etching, architectural details, restoration work.",
    services: ["Stencil etching", "Architectural details", "Monument restoration", "Custom projects"],
  },
];

const Industries = () => {
  return (
    <div data-testid="industries-page">
      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-surface">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              visible: { transition: { staggerChildren: 0.15 } },
            }}
            className="max-w-4xl"
          >
            <motion.span variants={fadeUp} transition={{ duration: 0.6 }} className="label-accent block mb-6">
              Industries
            </motion.span>
            <motion.h1
              variants={fadeUp}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-heading font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-text-primary leading-[0.95] tracking-tight mb-8"
            >
              Who We
              <br />
              <span className="text-text-secondary">Serve</span>
            </motion.h1>
            <motion.p
              variants={fadeUp}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-text-secondary text-lg md:text-xl max-w-2xl leading-relaxed"
            >
              From residential pool owners to commercial property managers. Precision media blasting for any client who demands quality results.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Split Feature - Residential vs Commercial */}
      <section className="section-spacing">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Residential */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              data-testid="industry-residential-feature"
              className="group relative overflow-hidden"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={images.blueTileAfter}
                  alt="Residential pool tile restoration"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <span className="label-accent block mb-2">Core Market</span>
                <h3 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-3">
                  Residential
                </h3>
                <p className="text-text-secondary mb-4 max-w-md">
                  Pool tile calcium removal, stone patio cleaning, and home surface renewal. Where precision meets pride of ownership.
                </p>
                <Link
                  to="/quote"
                  data-testid="residential-quote-link"
                  className="inline-flex items-center gap-2 text-accent font-mono uppercase tracking-widest text-sm hover:text-text-primary transition-colors"
                >
                  Get Residential Quote
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>

            {/* Commercial */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
              data-testid="industry-commercial-feature"
              className="group relative overflow-hidden"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={images.graffitiRemoval}
                  alt="Graffiti removal service"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <span className="label-accent block mb-2">Growth Market</span>
                <h3 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-3">
                  Commercial & Specialty
                </h3>
                <p className="text-text-secondary mb-4 max-w-md">
                  Property management, architectural surfaces, specialty projects. Scale without sacrificing quality.
                </p>
                <Link
                  to="/quote"
                  data-testid="commercial-quote-link"
                  className="inline-flex items-center gap-2 text-accent font-mono uppercase tracking-widest text-sm hover:text-text-primary transition-colors"
                >
                  Get Commercial Quote
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* All Industries Grid */}
      <section className="section-spacing bg-surface">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <span className="label-accent block mb-4">Who We Work With</span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-text-primary max-w-2xl">
              Every client. Same precision.
            </h2>
          </motion.div>

          {/* Trailer supporting image */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <div className="relative overflow-hidden">
              <img
                src={images.trailer}
                alt="Austin Media Blasting mobile service trailer"
                className="w-full h-auto max-h-[500px] object-contain mx-auto"
              />
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {industries.map((industry, index) => (
              <motion.div
                key={industry.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                data-testid={`industry-card-${index}`}
                className="card-service group"
              >
                <industry.icon className="w-8 h-8 text-accent mb-6" />
                <h3 className="font-heading font-bold text-xl text-text-primary mb-3">
                  {industry.title}
                </h3>
                <p className="text-text-secondary text-sm leading-relaxed mb-6">
                  {industry.description}
                </p>
                <ul className="space-y-2">
                  {industry.services.map((service) => (
                    <li key={service} className="text-text-secondary text-sm flex items-center gap-2">
                      <span className="w-1 h-1 bg-accent rounded-full" />
                      {service}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="label-accent block mb-6">Your Industry</span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-text-primary mb-6">
              Don't see your sector?
            </h2>
            <p className="text-text-secondary text-lg mb-10">
              If it has a surface, we can probably restore it. Reach out to discuss your specific needs.
            </p>
            <Link
              to="/contact"
              data-testid="industries-cta-contact"
              className="btn-primary inline-flex items-center gap-2"
            >
              Contact Us
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Industries;
